// Ported from hotwire-flow (MIT, @hotwirebits/flow) into conek-ui. The engine
// logic is upstream's; identifiers/events/classes follow the conek contract in
// docs/superpowers/specs/2026-09-09-flow-graph-components-design.md §2.
import { Controller } from "@hotwired/stimulus"

// Panel — positioned container for UI controls (like React Flow's <Panel />).
export default class extends Controller {
  static values = {
    position: { type: String, default: "top-left" }
  }

  connect() {
    this.element.classList.add("ui-flow-panel")
    this.element.style.position = "absolute"
    this.element.style.zIndex = "100"
    this._applyPosition()
    // Chrome-insets contract (UX audit): re-apply when any chrome widget
    // announces a change or the window resizes (rAF-gated, the directory's
    // one-frame idiom), then announce our own arrival so the engine
    // re-measures the strips.
    this._onChromeChange = () => this._applyPosition()
    this._onResize = () => this._scheduleApply()
    document.addEventListener("ui--flow:chromechange", this._onChromeChange)
    window.addEventListener("resize", this._onResize)
    this._announceChrome()
  }

  disconnect() {
    document.removeEventListener("ui--flow:chromechange", this._onChromeChange)
    window.removeEventListener("resize", this._onResize)
    if (this._applyRaf) { cancelAnimationFrame(this._applyRaf); this._applyRaf = null }
    this._announceChrome()
  }

  _applyPosition() {
    const pos = this.positionValue
    // Top positions inset below a toolbar bar; bottom positions inset above a
    // status bar (both bars pin to the canvas edges). The insets come from
    // the engine's chrome-insets contract (getChromeInsets) — the panel used
    // to measure .ui-flow-toolbar-bar/.ui-flow-status-bar itself; that hand
    // measurement is gone, the engine is the one source of truth.
    const insets = this._engine()?.getChromeInsets?.() || { top: 0, right: 0, bottom: 0, left: 0 }
    const top = `${16 + insets.top}px`
    const bottom = `${16 + insets.bottom}px`
    const positions = {
      "top-left":     { top, left: "16px" },
      "top-right":    { top, right: "16px" },
      "top-center":   { top, left: "50%", transform: "translateX(-50%)" },
      "bottom-left":  { bottom, left: "16px" },
      "bottom-right": { bottom, right: "16px" },
      "bottom-center":{ bottom, left: "50%", transform: "translateX(-50%)" }
    }
    Object.assign(this.element.style, positions[pos] || positions["top-left"])
  }

  // The panel declares no Stimulus outlet — resolve the engine off the
  // enclosing canvas element (the same element the outlet selectors name).
  _engine() {
    const canvas = this.element.closest(".ui-flow-canvas")
    if (!canvas) return null
    try { return this.application.getControllerForElementAndIdentifier(canvas, "ui--flow") } catch (e) { return null }
  }

  // Announce on connect/disconnect so the engine re-measures: a panel is not
  // a strip itself, but its arrival can follow a host-injected bar.
  _announceChrome() {
    const canvasId = this.element.closest(".ui-flow-canvas")?.id
    document.dispatchEvent(new CustomEvent("ui--flow:chromechange", { detail: { canvasId } }))
  }

  _scheduleApply() {
    if (this._applyRaf) return
    this._applyRaf = requestAnimationFrame(() => {
      this._applyRaf = null
      this._applyPosition()
    })
  }
}
